import React from 'react';
import { AlertTriangle, Target, Dumbbell, Camera } from 'lucide-react';
import VideoReportPlayer from './VideoReportPlayer';
import JointDiagram from './JointDiagram';
import { scoreBucket, toneClass, toneHex } from '../utils/coachLanguage';
import { jointDescription, jointTriplet } from '../utils/jointDescription';

/**
 * AnalyzerReport — render a saved movement analysis report.
 *
 * UI is now identical to live Sessions:
 *   • VideoReportPlayer (skeleton overlay, rep markers, problem-moment jump pills)
 *   • Hero ScoreRing (driven by unified score)
 *   • COACH'S TAKE paragraph
 *   • One per-movement card with Tech / Balance / Pace sub-scores + plain-English joint rows
 *
 * The legacy `report` field (overall_score / findings / risks / recommendations /
 * next_session) is no longer rendered. It produced contradictory numbers — a
 * different metric vocabulary clashing with the unified Sessions readout —
 * and burnt a second Claude call per analysis. Old saved analyses without
 * sessions_analysis simply won't render score cards (the user can re-record).
 */

const scoreColor = (n) => {
  if (n == null) return '#71717a';
  if (n >= 85) return '#22d3ee';
  if (n >= 70) return '#CCFF00';
  if (n >= 55) return '#facc15';
  return '#f87171';
};

const angleStatusText = (s) => {
  if (s === 'good') return 'in range';
  if (s === 'warning') return 'close';
  if (s === 'off') return 'out of range';
  return '';
};

const viewLabel = (v) => {
  if (v === 'front') return 'Front view';
  if (v === 'side') return 'Side view';
  if (v === 'angled') return 'Angled view';
  return 'Camera view unknown';
};

const ScoreRing = ({ score, label }) => {
  const r = 48;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score ?? 0));
  const offset = c * (1 - pct / 100);
  const color = scoreColor(score);
  return (
    <div className="relative w-32 h-32 flex items-center justify-center" data-testid="analyzer-score-ring">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} stroke="#27272a" strokeWidth="8" fill="none" />
        <circle
          cx="60" cy="60" r={r}
          stroke={color} strokeWidth="8" fill="none"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(.2,.8,.2,1)' }}
        />
      </svg>
      <div className="relative text-center">
        <div className="font-secondary font-bold text-3xl" style={{ color }}>{score ?? '—'}</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{label || ''}</div>
      </div>
    </div>
  );
};

const SessionsExerciseCard = ({ sessions, movementLabel }) => {
  const score = sessions?.score != null ? Math.round(sessions.score) : null;
  const bk = scoreBucket(score);
  const techN = sessions?.technique != null ? Math.round(sessions.technique) : null;
  const balN = sessions?.balance != null ? Math.round(sessions.balance) : null;
  const paceN = sessions?.pace != null ? Math.round(sessions.pace) : null;
  const techBk = scoreBucket(techN);
  const balBk = scoreBucket(balN);
  const paceBk = scoreBucket(paceN);
  const angles = Array.isArray(sessions?.joint_angles) ? sessions.joint_angles : [];
  const viewsCaptured = sessions?.views_captured || {};
  const nothingFromFront = !Object.keys(viewsCaptured).includes('front');
  const nothingFromSide = !Object.keys(viewsCaptured).includes('side');
  const hiddenHints = [];
  if (nothingFromFront) hiddenHints.push('symmetry');
  if (nothingFromSide) hiddenHints.push('depth angles');
  const exerciseBreakdown = sessions?.narrative?.exercise_breakdown?.[0] || null;

  return (
    <div
      className="hpme-card p-6 border-l-4"
      style={{ borderLeftColor: score !== null ? toneHex(bk.tone) : '#3f3f46' }}
      data-testid="analyzer-exercise-card"
    >
      <div className="hpme-card-corner" />

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Dumbbell className="w-5 h-5 text-primary" />
          <h3 className="font-secondary font-bold text-xl">{movementLabel}</h3>
        </div>
        <span className={`font-secondary font-bold ${toneClass(bk.tone)}`} data-testid="analyzer-exercise-tag">
          {bk.label}
          {score !== null && (
            <>
              {' '}
              <span className="text-zinc-400 font-mono text-sm ml-2">{score}/100</span>
            </>
          )}
        </span>
      </div>

      {Object.keys(viewsCaptured).length > 0 && (
        <div className="flex items-center gap-1.5 mb-3 text-xs text-zinc-500 flex-wrap" data-testid="analyzer-exercise-views">
          <Camera className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="text-zinc-500">Filmed from:</span>
          {Object.entries(viewsCaptured).filter(([v]) => v && v !== 'unknown').map(([v, n], i, arr) => (
            <span key={v} className="text-zinc-300 font-semibold">
              {viewLabel(v).replace(' view', '')}{n ? ` (${n})` : ''}
              {i < arr.length - 1 && <span className="text-zinc-600 font-normal"> · </span>}
            </span>
          ))}
        </div>
      )}

      {score !== null && (
        <div className="grid grid-cols-3 gap-3 mb-3 pb-4 border-b border-zinc-800" data-testid="analyzer-subscores">
          <div title="How close your joint angles stayed to the ideal coaching range across reps. Any joint outside its range caps this score.">
            <p className="text-[10px] text-zinc-500 uppercase cursor-help flex items-center gap-1">
              Technique <span className="text-zinc-600">ⓘ</span>
            </p>
            <p className={`font-semibold text-sm ${toneClass(techBk.tone)}`}>
              {techBk.label}
              {techN !== null && <> <span className="text-zinc-500 font-mono text-xs ml-1">{techN}/100</span></>}
            </p>
          </div>
          <div title="How evenly your left and right sides moved — only measurable from the front view.">
            <p className="text-[10px] text-zinc-500 uppercase cursor-help flex items-center gap-1">
              Balance <span className="text-zinc-600">ⓘ</span>
            </p>
            <p className={`font-semibold text-sm ${toneClass(balBk.tone)}`}>
              {balN === null ? 'N/A from this view' : balBk.label}
              {balN !== null && <> <span className="text-zinc-500 font-mono text-xs ml-1">{balN}/100</span></>}
            </p>
          </div>
          <div title="Your rep tempo vs a normal controlled rep (~2 seconds). Too fast or too slow loses points.">
            <p className="text-[10px] text-zinc-500 uppercase cursor-help flex items-center gap-1">
              Pace <span className="text-zinc-600">ⓘ</span>
            </p>
            <p className={`font-semibold text-sm ${toneClass(paceBk.tone)}`}>
              {paceN === null ? '—' : paceBk.label}
              {paceN !== null && <> <span className="text-zinc-500 font-mono text-xs ml-1">{paceN}/100</span></>}
            </p>
          </div>
        </div>
      )}

      {angles.length > 0 && (
        <div className="space-y-2 mb-3">
          {angles.map((a, aidx) => {
            const sTone = a.status === 'good' ? 'excellent'
              : a.status === 'warning' ? 'average'
              : a.status === 'off' ? 'poor' : 'neutral';
            const st = angleStatusText(a.status);
            const desc = jointDescription(a.label, a.phase);
            const triplet = jointTriplet(a.label);
            return (
              <div key={aidx} className="bg-zinc-800/50 p-3 rounded-sm flex items-start gap-4" data-testid={`analyzer-angle-${aidx}`}>
                <JointDiagram label={a.label} size={36} />
                <div className="flex-1 min-w-0">
                  <p className="font-secondary font-bold text-sm uppercase tracking-wide text-zinc-100 flex items-center gap-2 flex-wrap">
                    <span>{a.label}</span>
                    {a.phase && a.phase !== 'throughout' && (
                      <span
                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider ${
                          a.phase === 'at_bottom'
                            ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                            : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                        }`}
                      >
                        {a.phase === 'at_bottom' ? 'BOTTOM' : a.phase === 'at_top' ? 'TOP' : a.phase}
                      </span>
                    )}
                    {a.phase === 'throughout' && (
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider bg-zinc-700/40 text-zinc-300 border border-zinc-600/40">
                        WHOLE REP
                      </span>
                    )}
                    {a.view && (
                      <span
                        className={`text-[10px] font-mono px-1.5 py-0.5 rounded uppercase tracking-wider border ${
                          a.view === 'front'
                            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                            : a.view === 'side'
                              ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                              : 'bg-zinc-700/40 text-zinc-300 border-zinc-600/40'
                        }`}
                        title={`This row was measured from your ${a.view}-view clip.`}
                      >
                        {a.view.toUpperCase()}
                      </span>
                    )}
                  </p>
                  {triplet && (
                    <p className="text-zinc-300 text-xs mt-0.5">{triplet}</p>
                  )}
                  {desc && (
                    <p className="text-zinc-400 text-xs mt-1 leading-relaxed">{desc}</p>
                  )}
                  <div className="flex items-baseline gap-3 mt-2 flex-wrap">
                    <span className="text-xs text-zinc-500">Measured</span>
                    <span className={`font-mono text-lg ${toneClass(sTone)}`}>{Math.round(a.value)}°</span>
                    <span className="text-xs text-zinc-500">Ideal</span>
                    <span className="font-mono text-sm text-zinc-300">{a.ideal_min}–{a.ideal_max}°</span>
                    {st && <span className={`text-xs ${toneClass(sTone)}`}>· {st}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hiddenHints.length > 0 && (() => {
        const tip = nothingFromFront && nothingFromSide
          ? 'film one set from the side and one from the front next time to unlock symmetry + depth metrics'
          : nothingFromFront
            ? 'film one set from the front next time to unlock symmetry, knee tracking, and elbow flare'
            : 'film one set from the side next time to unlock joint depth and back angle';
        return (
          <div className="flex items-start gap-2 mb-3 text-xs text-zinc-500 bg-zinc-800/30 px-3 py-2 rounded-sm" data-testid="analyzer-filtered-by-view">
            <Camera className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              <span className="text-zinc-400 font-semibold">{hiddenHints.join(' & ')}</span> not captured — {tip}.
            </span>
          </div>
        );
      })()}

      {exerciseBreakdown?.feedback && (
        <p className="text-zinc-300 text-sm mb-2" data-testid="analyzer-exercise-feedback">{exerciseBreakdown.feedback}</p>
      )}
      {exerciseBreakdown?.key_issue && (
        <div className="flex items-start gap-2 mt-2">
          <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
          <span className="text-yellow-400 text-sm">{exerciseBreakdown.key_issue}</span>
        </div>
      )}
      {exerciseBreakdown?.cue && (
        <div className="mt-3 bg-primary/10 border border-primary/20 px-3 py-1.5 rounded-sm" data-testid="analyzer-exercise-cue">
          <span className="text-xs text-zinc-500 uppercase">Next session cue: </span>
          <span className="text-primary text-sm font-semibold">{exerciseBreakdown.cue}</span>
        </div>
      )}
    </div>
  );
};

const AnalyzerReport = ({ analysis, videoURL = null, playerFrames = null }) => {
  if (!analysis) return null;
  const movementLabel = analysis.custom_movement_name || analysis.movement_label || analysis.movement_id;
  const metrics = analysis.metrics || {};
  const sessions = analysis.sessions_analysis || null;
  const coachTake = sessions?.narrative?.detailed_feedback || null;

  // Player data: prefer fresh in-memory frames; fall back to saved keyframes
  const frames = playerFrames || analysis.landmarks_keyframes || [];
  const repTimestamps = metrics.rep_timestamps || [];
  const problemMoments = metrics.problem_moments || [];
  const showPlayer = frames.length > 0 || !!videoURL;

  const overallScore = sessions?.score != null ? Math.round(sessions.score) : null;
  const overallLabel = overallScore != null ? scoreBucket(overallScore).label : '';

  return (
    <div className="space-y-6">
      {/* Video / skeleton playback — UNCHANGED */}
      {showPlayer && (
        <div className="hpme-card p-4 md:p-6" data-testid="analyzer-player-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-secondary font-bold tracking-tight text-lg">Movement Replay</h3>
            {!videoURL && (
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                Skeleton only · video deleted with analysis
              </span>
            )}
          </div>
          <VideoReportPlayer
            videoURL={videoURL}
            frames={frames}
            duration={analysis.duration_seconds || (frames.length ? frames[frames.length - 1].t : 0)}
            width={analysis.video_width || 480}
            height={analysis.video_height || 360}
            repTimestamps={repTimestamps}
            problemMoments={problemMoments}
          />
        </div>
      )}

      {/* Hero: score + meta */}
      <div className="hpme-card p-6">
        <div className="hpme-card-corner" />
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <ScoreRing score={overallScore} label={overallLabel} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-primary">Movement Analysis</div>
            <h2 className="font-secondary font-bold text-2xl md:text-3xl tracking-tight mt-1" data-testid="analyzer-movement-label">
              {movementLabel}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-zinc-400">
              {metrics.rep_count != null && (
                <span className="px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800">
                  {metrics.rep_count} rep{metrics.rep_count === 1 ? '' : 's'}
                </span>
              )}
              {metrics.view_detected && (
                <span className="px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800 capitalize">
                  {metrics.view_detected} view
                </span>
              )}
              {analysis.duration_seconds != null && (
                <span className="px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800">
                  {Math.round(analysis.duration_seconds * 10) / 10}s clip
                </span>
              )}
              {analysis.frames_processed != null && (
                <span className="px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800">
                  {analysis.frames_processed} frames
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* COACH'S TAKE — same voice/tone as live Sessions debrief */}
      {coachTake && (
        <div className="hpme-card p-6" data-testid="analyzer-coach-take">
          <div className="hpme-card-corner" />
          <h3 className="font-secondary font-bold text-xl mb-3 flex items-center gap-2">
            <Target className="w-5 h-5 text-accent-purple" />
            COACH'S TAKE
          </h3>
          <p className="text-zinc-300 leading-relaxed">{coachTake}</p>
        </div>
      )}

      {/* Sessions-style per-movement card with sub-scores + plain-English joint rows */}
      {sessions && <SessionsExerciseCard sessions={sessions} movementLabel={movementLabel} />}
    </div>
  );
};

export default AnalyzerReport;
